import { useCallback, Fragment, useState, useEffect } from 'react';
import { ActionIcon, Modal, createStyles, Button, Loader, Alert, Group, Text, useMantineTheme, List, Code, Space } from '@mantine/core';
import { IconArrowLeft, IconDownload, IconExternalLink, IconAlertCircle, IconCloudUpload, IconX, IconCircleCheck } from '@tabler/icons-react';
import { Dropzone } from '@mantine/dropzone';
import { downloadJSON, requireAccountToAccessSandbox, getCopyLink } from '../../assets/constants';

const useStyles = createStyles((theme) => ({
    dropzone: {
        position: 'relative',
        background: 'white !important',
        '&[data-accept]': {
            background: 'white',
        },

        '&[data-reject]': {
            background: 'white'
        },
    },

    dropzoneDisabled: {
        backgroundColor: theme.colors.gray[0],
        borderColor: theme.colors.gray[2],
        cursor: 'not-allowed',

        '& *': {
            color: theme.colors.gray[5],
        },
    },
}));


const exampleJSON = `[
    {
        "question": "What's your favorite color?",
        "options": ["Red", "Blue", "Green"],
        "descriptions": ["Male", "Unemployed"],
        "years": [2023]
    },
    {
        "question": "What's your favorite animal?",
        "options": ["Dog", "Cat", "Fish"],
        "years": [2001, 2002, 2003]
    }
]`

const checkIfJsonOk = (jsons) => {
    // Make sure jsons is an array
    if (!Array.isArray(jsons)) return false;
    // Make sure jsons has at least one element
    if (jsons.length < 1) return false;
    // Make sure each json object in the array is in the correct format
    return jsons.every(json => {
        // Check if the json is an object (not required to be an array itself)
        if (typeof json !== 'object') return false;
        // Make sure element has a question string
        if (typeof json.question !== 'string') return false;
        // Make sure element has an array of options
        if (!Array.isArray(json.options)) return false;
        // Make sure options array has at least two elements
        if (json.options.length < 2) return false;
        // Make sure options array is all strings
        if (json.options.some(option => typeof option !== 'string')) return false;
        // If it has a description field, make sure it's an array
        if (json.descriptions) {
            if (!Array.isArray(json.descriptions)) return false;
            // If all elements are not all strings, return false
            if (json.descriptions.some(description => typeof description !== 'string')) return false;
        }
        // Make sure years is an array of integers
        if (json.years && !Array.isArray(json.years)) return false;
        if (json.years && json.years.some(year => typeof year !== 'number')) return false;
        return true;
    });
}

const uploadJSONFile = async (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const text = (e.target.result);
                const json = JSON.parse(text);
                resolve(json);
            } catch (error) {
                reject(error);
            }
        };
        reader.onerror = reject;
        reader.readAsText(file);
    });
}


const uploadJSONFiles = async (files) => {
    let jsonFiles = [];
    let filenames = [];
    for (let file of files) {
        try {
            const json = await uploadJSONFile(file);
            jsonFiles.push(json);
            // Update filenames
            filenames.push(file.name);
        } catch (error) {
            console.error("An error occurred while reading the file:", error);
        }
    }
    // Flatten the array of arrays
    jsonFiles = [].concat(...jsonFiles);
    return [jsonFiles, filenames];
}

const formatQuestion = (obj, currentUserId) => {
    const minYear = Math.min(...obj.years);
    const maxYear = Math.max(...obj.years);
    return {
        isPanel: obj.years.length > 1,
        numSurveysInBatch: obj.years.length,
        surveyYears: [minYear, maxYear],
        questionType: 'Multiple choice',
        surveyAnswers: obj.options,
        surveyCountry: 'United States',
        surveyDescriptions: obj.descriptions ? obj.descriptions : [],
        surveyQuestion: obj.question,
        surveyModel: 'GSS-v1.1',
        customQuestionsAndResponses: [],
        userId: currentUserId,
    };
}

const getTotalNumSurveys = (questionsArray) => {
    return questionsArray.reduce((acc, curr) => {
        return acc + curr.years.length;
    }, 0);
}

const FileUpload = ({
    currentUser,
    userSurveyHistory,
    setUserSurveyHistory,
    numCreditsAvailable,
    updateCreditUsage,
    baseURL,
    fileModalOpened,
    setFileModalOpened,
}) => {

    const theme = useMantineTheme();
    const [wrongFileType, setWrongFileType] = useState(false);
    const [wrongFileFormat, setWrongFileFormat] = useState(false);
    const [filenames, setFilenames] = useState([]);
    const [surveyLinks, setSurveyLinks] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [surveyFetchError, setSurveyFetchError] = useState(false);
    const [overCreditLimit, setOverCreditLimit] = useState(false);
    const [numQuestionsInBatch, setNumQuestionsInBatch] = useState(0);
    const [generatedSurveys, setGeneratedSurveys] = useState([]);
    const [ranSuccessfully, setRanSuccessfully] = useState(false);
    const [allowFileUploadExit, setAllowFileUploadExit] = useState(true);

    const { cx, classes } = useStyles();


    const generateSurveyLinks = async (questionObjects) => {
        const userId = currentUser ? currentUser.uid : 'anon';
        const email = currentUser ? currentUser.email : '';
        const formattedQuestions = questionObjects.map(q => formatQuestion(q, userId));
        const loadedSurveyData = await Promise.all(formattedQuestions.map(q => runSurveyAndGetLink(q, email)));
        const newGeneratedSurveys = loadedSurveyData.map(({ surveyResponse }) => surveyResponse);
        setGeneratedSurveys(newGeneratedSurveys);
        const loadedSurveyLinks = loadedSurveyData.map(({ copyLink }) => copyLink);
        // If any of the links are empty, then there was an error
        if (loadedSurveyLinks.some(link => link === '')) {
            setSurveyFetchError(true);
        } else {
            setUserSurveyHistory([...newGeneratedSurveys, ...userSurveyHistory]);
            setSurveyLinks(loadedSurveyLinks);
            setRanSuccessfully(true);
        }
    };

    const runSurveyAndGetLink = async (question, email) => {
        const dataForSimulationFetch = { 
            ...question,
            origin: 'file-upload',
            updateUserCredits: requireAccountToAccessSandbox
        };
        try {
            // Run surveyresponse
            const results = await fetch("/.netlify/functions/runSimulation", { method: "POST", body: JSON.stringify({ ...dataForSimulationFetch, email }) });
            const json_results = await results.json();
            if (results.ok) {
                let { surveyData, date, id } = json_results;
                const surveyResponse = { ...question, surveyData, id, date };
                const fetchBodyForLink = {
                    surveyData: surveyResponse,
                    surveyId: id,
                }
                await fetch("/.netlify/functions/storeSurveyLink", { method: "POST", body: JSON.stringify(fetchBodyForLink) });
                const copyLink = getCopyLink(surveyResponse, baseURL);
                return { copyLink, surveyResponse };
            } else {
                console.log('error', json_results);
                return '';
            }
        } catch (error) {
            console.log('error', error);
            return '';
        }
    }

    const resetState = () => {
        setRanSuccessfully(false);
        setNumQuestionsInBatch(0);
        setOverCreditLimit(false);
        setFilenames([]);
        setSurveyLinks([]);
        setQuestions([]);
        setWrongFileType(false);
        setWrongFileFormat(false);
        setSurveyFetchError(false);
    }

    const handleDownload = async (files) => {
        resetState();
        const [loadedJSON, loadedFilenames] = await uploadJSONFiles(files);
        runSurveysUsingJSONData(loadedJSON, loadedFilenames);
    }

    const runSurveysUsingJSONData = async (loadedJSON, loadedFilenames) => {
        const filesOk = checkIfJsonOk(loadedJSON);
        if (!filesOk) {
            setWrongFileFormat(true);
            setFilenames(loadedFilenames);
            return
        }
        setNumQuestionsInBatch(loadedJSON.length)
        const numSurveysInBatch = getTotalNumSurveys(loadedJSON);
        if (numSurveysInBatch > numCreditsAvailable) {
            setOverCreditLimit(true);
            return;
        }
        setAllowFileUploadExit(false);
        setFilenames(loadedFilenames);
        setQuestions(loadedJSON);
        await generateSurveyLinks(loadedJSON);
        setAllowFileUploadExit(true);
        updateCreditUsage(numSurveysInBatch);
    }

    const dropZoneOverlayStyes = {
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: '0',
        left: '0',
        borderRadius: theme.radius.lg
    };

    const handlePaste = useCallback((event) => {
        if (allowFileUploadExit && ranSuccessfully === false) {
            resetState();
            let pasteData = event.clipboardData || window.clipboardData;
            if (pasteData) {
                let pastedText = pasteData.getData('text');
                try {
                    const parsedJSON = JSON.parse(pastedText);
                    runSurveysUsingJSONData(parsedJSON, ['data']);
                }
                catch (error) {
                    console.log('error', error);
                    setWrongFileType(true);
                    setFilenames(['data'])
                }
            }
        }
    }, [allowFileUploadExit, ranSuccessfully]);

    useEffect(() => {
        if (fileModalOpened) {
            window.addEventListener('paste', handlePaste);
        } else {
            window.removeEventListener('paste', handlePaste);
            resetState();
        }
    
        return () => {
            window.removeEventListener('paste', handlePaste);
        };
    }, [fileModalOpened, handlePaste]);


    return (
        <Modal
            opened={fileModalOpened}
            radius='lg'
            // title='Upload questions'
            title={
                ranSuccessfully ?
                    <Group spacing='xs' sx={{ height: '28px' }}>
                        <ActionIcon onClick={resetState}>
                            <IconArrowLeft />
                        </ActionIcon>
                        <div>Restart</div>
                    </Group>
                    :
                    <Group spacing='xs' sx={{ height: '28px' }}>
                        <div>Upload questions</div>
                    </Group>
            }
            centered
            padding='xl'
            size='500px'
            className={classes.modal}
            onClose={() => {
                if (allowFileUploadExit) setFileModalOpened(false);
            }}
            withCloseButton={allowFileUploadExit}
        >
            {
                ranSuccessfully
                    ?
                    <>
                        <Fragment>
                            <Alert
                                color="gray"
                                // variant = 'filled'
                                radius='md'
                                icon={<IconCircleCheck size="1rem" />}
                            >
                                {numQuestionsInBatch === 1 ? 'Link' : 'Links'} generated successfully
                            </Alert>
                        </Fragment>
                        <Space h='md' />
                        {
                            surveyLinks.map((link, i) => (
                                <Fragment key={`link-${i}`}>
                                    <Button
                                        component='a'
                                        href={link}
                                        target='_blank'
                                        // color='teal'
                                        radius='md'
                                        sx={{ width: '100%' }}
                                        variant='outline'
                                        leftIcon={<IconExternalLink size={17.5} stroke={1.5} />}
                                    >
                                        <Text component='span' style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            fontWeight: '400',
                                        }}>
                                            {questions[i].question}
                                        </Text>
                                    </Button>
                                    <Space h='xs' />
                                </Fragment>
                            ))
                        }
                        <Space h='xs' />
                        <Group position='center'>
                            <Button
                                size='sm'
                                radius='md'
                                variant='subtle'
                                leftIcon={<IconDownload size={17.5} stroke={1.5} />}
                                onClick={() => downloadJSON(generatedSurveys)}
                            >
                                Download data
                            </Button>
                            <Text color='gray' size='sm' ta='left'>
                                Data is stored in your survey history.
                            </Text>
                        </Group>
                    </>
                    :
                    <>
                        <Dropzone
                            onDrop={async (files) => await handleDownload(files)}
                            onReject={(files) => {
                                setWrongFileType(true);
                                const failedFilenames = files.map(file => file.file.name);
                                setFilenames(failedFilenames);
                            }}
                            multiple={false}
                            radius='lg'
                            accept={['application/json']}
                            disabled={allowFileUploadExit === false}
                            className={allowFileUploadExit ? classes.dropzone : cx(classes.dropzone, classes.dropzoneDisabled)}
                        >
                            <Dropzone.Reject>
                                <div style={{ ...dropZoneOverlayStyes, background: 'rgba(255,0,0,0.05)' }} />
                            </Dropzone.Reject>
                            <Dropzone.Accept>
                                <div style={{ ...dropZoneOverlayStyes, background: 'rgba(0,0,0,0.05)' }} />
                            </Dropzone.Accept>
                            <Group position="center">
                                <Dropzone.Accept>
                                    <IconCloudUpload
                                        size={40}
                                        stroke={1.5}
                                        color={theme.colors[theme.primaryColor][6]}
                                    />
                                </Dropzone.Accept>
                                <Dropzone.Reject>
                                    <IconX
                                        size={40}
                                        stroke={1.5}
                                        color={theme.colors.red[6]}
                                    />
                                </Dropzone.Reject>
                                <Dropzone.Idle>
                                    <IconCloudUpload
                                        size={40}
                                        stroke={1.5}
                                        color={allowFileUploadExit ? theme.colors[theme.primaryColor][6] : theme.colors.gray[5]}
                                    />
                                </Dropzone.Idle>
                                <div>
                                    <Text size="lg" ta='center'>
                                        Upload a JSON file or
                                    </Text>
                                    <Text size="lg" ta='center'>
                                        paste anywhere on this page
                                    </Text>
                                    <Space h='sm' />
                                    <Text size="md" inline mt={7}>
                                        Data should be a JSON array with the following fields for each question:
                                    </Text>
                                    <Space h={5} />
                                    <List size='md' withPadding>
                                        <List.Item><Code component='span'>question</Code> (string)</List.Item>
                                        <List.Item><Code>options</Code> (array)</List.Item>
                                        <List.Item><Code>descriptions</Code> (array of strings)</List.Item>
                                        <List.Item><Code>years</Code> (array of integers)</List.Item>
                                    </List>
                                </div>
                            </Group>
                            <Space h='xs' />
                        </Dropzone>
                        <Space h='xs' />
                        {
                            filenames.map((filename, i) => (
                                <Fragment key={`filename-${i}`}>
                                    <Space h='xs' />
                                    {
                                        wrongFileType ?
                                            <Alert
                                                title={`Failed to upload ${filename}`}
                                                color="red"
                                                radius='md'
                                                icon={<IconAlertCircle size="1rem" />}
                                            >
                                                <Text>Please upload or paste a JSON file.</Text>
                                            </Alert>
                                            :
                                            wrongFileFormat ?
                                                <Alert
                                                    title={`Failed to upload ${filename}`}
                                                    color="red"
                                                    radius='md'
                                                    icon={<IconAlertCircle size="1rem" />}
                                                >
                                                    <Text>Make sure your JSON file conforms to the format specified above.</Text>
                                                    <Text>For example, here is an acceptable JSON file:</Text>
                                                    <Space h='xs' />
                                                    <Code block>{exampleJSON}</Code>
                                                </Alert>
                                                :
                                                <>
                                                    <Alert
                                                        color="gray"
                                                        radius='md'
                                                        icon={<IconCircleCheck size="1rem" />}
                                                    >
                                                        {`Uploaded ${filename}`}
                                                    </Alert>
                                                    <Space h='xs' />
                                                    {
                                                        surveyLinks.length === 0 &&
                                                            surveyFetchError ?
                                                            <Alert
                                                                // title={`Failed to generate survey links`}
                                                                color="red"
                                                                radius='md'
                                                                icon={<IconAlertCircle size="1rem" />}
                                                            >
                                                                Failed to generate survey {numQuestionsInBatch === 1 ? 'link' : 'links'}. Please try again.
                                                            </Alert>
                                                            :
                                                            overCreditLimit ?
                                                                <Alert
                                                                    // title={`Failed to generate survey links`}
                                                                    color="red"
                                                                    radius='md'
                                                                    icon={<IconAlertCircle size="1rem" />}
                                                                >
                                                                    You do not have enough credits to run {numQuestionsInBatch === 1 ? 'this question' : 'these questions'}.
                                                                    Please contact support at <a style={{ color: 'black' }} href="mailto:support@roundtable.ai">support@roundtable.ai</a> to upgrade your plan.
                                                                </Alert>
                                                                :
                                                                <>
                                                                    <Space h='xs' />
                                                                    <Group position="center">
                                                                        <Loader size='sm' />
                                                                        <Text>
                                                                            Generating {numQuestionsInBatch === 1 ? 'link' : 'links'}...
                                                                        </Text>
                                                                    </Group>
                                                                </>
                                                    }
                                                </>
                                    }
                                </Fragment>
                            ))
                        }
                    </>
            }
        </Modal>
    )
};

export default FileUpload;